import type { InspectorDTO } from "src/types/inspector";

import React, { useMemo, useState } from "react";

import { Box } from "@mui/material";

import { useUserGeoFromIp } from "src/hooks/use-user-geo-from-ip";
import { parseLatLngFromLocationString } from "src/utils/geo";
import { shouldBlockGoogleMapsMount } from "src/utils/user-geo-from-ip-state";

import { LocationMap } from "../map/location-map";

export const StaticMap = React.memo(
  ({
    location,
    inspectors,
    onInspectorSelect,
    highlightedMarkerId,
    selectedIds,
  }: {
    location: string | undefined;
    inspectors: InspectorDTO[];
    onInspectorSelect: (inspectorId: number) => void;
    highlightedMarkerId?: number;
    selectedIds?: number[];
  }) => {
    const userGeo = useUserGeoFromIp();
    const [selectedMarker, setSelectedMarker] = useState<InspectorDTO | null>(null);

    const markers = useMemo(() => {
      const siteCoords = parseLatLngFromLocationString(location);
      const siteMarker = siteCoords
        ? {
            position: siteCoords,
            title: 'Site Location',
            draggable: false,
          }
        : null;

      const houseIcon = {
        url: 'https://api.iconify.design/material-symbols:home-pin-rounded.svg?color=%234285F4&width=32&height=32',
      };

      const inspectorMarkers = inspectors.flatMap((inspector) => {
        const position = parseLatLngFromLocationString(inspector.location);
        if (!position) {
          return [];
        }

        return [
          {
            position,
            title: inspector.name,
            draggable: false,
            icon: houseIcon,
            onClick: () => {
              setSelectedMarker(inspector);
            },
            id: inspector.id,
            inspectorData: inspector,
          },
        ];
      });

      return siteMarker ? [siteMarker, ...inspectorMarkers] : inspectorMarkers;
    }, [inspectors, location]);

    if (shouldBlockGoogleMapsMount(userGeo) || markers.length === 0) {
      return null;
    }

    return (
      <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <LocationMap
          defaultLocation={location}
          viewOnly
          markers={markers}
          onInspectorSelect={onInspectorSelect}
          highlightedMarkerId={highlightedMarkerId}
          selectedIds={selectedIds}
          selectedMarker={selectedMarker}
          onMarkerSelect={setSelectedMarker}
        />
      </Box>
    );
  }
);
